import React from 'react';
import { AlertTriangle, CheckCircle } from 'lucide-react';
import type { CheckDrugInteractionsWidgetData } from '../../../types/agentWidgets';

interface Props {
  data: CheckDrugInteractionsWidgetData;
}

function severityColor(severity?: string): string {
  if (!severity) return 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400';
  const s = severity.toLowerCase();
  if (s.includes('high') || s.includes('major')) return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
  if (s.includes('moderate') || s.includes('medium')) return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
  return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
}

export const CheckDrugInteractionsWidget: React.FC<Props> = ({ data }) => {
  const hasInteractions = data.interactions.length > 0;

  return (
    <div className="mt-1.5 space-y-1">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500 font-medium">
        <span>{data.drug1} + {data.drug2}</span>
      </div>

      {hasInteractions ? (
        <div className="space-y-1">
          {data.interactions.map((ix, i) => (
            <div
              key={i}
              className="text-[11px] bg-rose-50/70 dark:bg-rose-900/10 rounded px-2.5 py-1.5 space-y-0.5"
            >
              <div className="flex items-start gap-1.5">
                <AlertTriangle className="h-3 w-3 text-rose-500 dark:text-rose-400 flex-shrink-0 mt-0.5" />
                <p className="text-rose-700 dark:text-rose-300 flex-1 min-w-0">
                  {ix.description}
                </p>
              </div>
              {ix.severity && (
                <span className={`inline-block text-[9px] px-1.5 py-0.5 rounded-full font-medium ${severityColor(ix.severity)}`}>
                  {ix.severity}
                </span>
              )}
            </div>
          ))}
          <p className="text-[9px] text-gray-400 dark:text-gray-500 italic">
            US database (RxNorm). Verify with BNF for UK practice.
          </p>
        </div>
      ) : (
        <div className="flex items-center gap-1.5 text-[11px] bg-green-50/70 dark:bg-green-900/10 rounded px-2.5 py-1.5">
          <CheckCircle className="h-3 w-3 text-green-500 dark:text-green-400" />
          <span className="text-green-700 dark:text-green-300">No known interactions found</span>
        </div>
      )}
    </div>
  );
};
